import { Enquiry } from "../components/Enquiry";
import { Footer } from "../components/Footer";
import { Layout } from "../components/Layout";
import { RewardsBadgeA, RewardsBadgeB, RewardsBadgeC } from "../components/RewardsBadges";
import { CTA_LINK, CTA_TEXT, DEMO_TEXT } from "@/components/layout/PageLayout";
import { Card, Carousel, CarouselItemWrapper } from "@/components/layout/sections/CardCarousel";
import { CaseStudyGrid } from "@/components/layout/sections/CaseStudyGrid";
import { Hero } from "@/components/layout/sections/Hero";
import { LetsBuildFooter } from "@/components/layout/sections/ProductFooter/ProductFooter";
import Section from "@/components/layout/sections/Section";
import Subtitle from "@/components/layout/text/Subtitle/Subtitle";
import Title from "@/components/layout/text/Title/Title";
import TitleSection from "@/components/layout/text/TitleSection/TitleSection";
import { faHandsHoldingDiamond, faKey, faLock, faPalette, faStar, faWrench } from "@icons/regular";
import { Button, Icon } from "@ui";
import { Link, graphql } from "gatsby";
import { StaticImage } from "gatsby-plugin-image";
import * as React from "react";
import * as ReactDOM from "react-dom";
import { Helmet } from "react-helmet";

interface IProps {
  location: Record<any, string>;
  data: Record<any, any>;
}

const TESTIMONIAL = {
  text: "The first story we did was on The Lancet, which is one of our publications. After posting that story, we immediately saw a threefold increase in the number of reads and a 50% increase in dwell time.",
  author: "Paul Abrahams, RELX",
};

const whyShorthand = [
  {
    id: 1,
    title: "Eliminate creative bottlenecks",
    description: "Empower non-technical teams to deliver high-impact, visually engaging content without developer overheads.",
  },
  {
    id: 2,
    title: "Guarantee brand consistency",
    description: "Lock in brand standards across every story with reusable, custom themes and templates.",
  },
  {
    id: 3,
    title: "Scale effortlessly with your organisation",
    description: "Manage users centrally with SSO, SAML, and SCIM. Define custom roles and permissions for team workflows.",
  },
  {
    id: 4,
    title: "Meet enterprise-grade security and compliance",
    description: "Certified to ISO 27001 and SOC 2 standards, with full WCAG accessibility built in.",
  },
  {
    id: 5,
    title: "Integrate seamlessly with your tech stack",
    description: "Shorthand connects with your existing tools, from CMSes to analytics, to keep publishing fast and frictionless.",
  },
];

const carouselItems = [
  {
    id: 1,
    title: "Auto layout",
    description:
      "Kickstart your content creation or spark ideas. Upload existing content — from web pages or PDF files — and let our AI auto-layout tool structure it beautifully in seconds.",
    imageUrl: "/media/enterprise/auto-layout.webp",
    imageAlt: "A screenshot of the auto layout feature in the Shorthand editor",
  },
  {
    id: 2,
    title: "Custom themes",
    description:
      "Lock in your brand’s visual identity and set global styles to align content across your organisation. Standardise colours, fonts, logos, and more.",
    imageUrl: "/media/enterprise/custom-themes.webp",
    imageAlt: "A palette of styles showing the configuration of Shorthand custom themes",
  },
  {
    id: 3,
    title: "Custom code",
    description:
      "Have full creative control by adding custom HTML, CSS, and JavaScript to your stories. This flexibility allows for unique visual treatments and seamless integration with third-party tools.",
    imageUrl: "/media/enterprise/custom-code.webp",
    imageAlt: "A screenshot of the custom html block in the Shorthand editor",
  },
  {
    id: 4,
    title: "Secure publishing",
    description:
      "Kickstart your content creation or spark ideas. Upload existing content — from web pages or PDF files — and let our AI auto-layout tool structure it beautifully in seconds.",
    imageUrl: "/media/enterprise/secure-publishing.webp",
    imageAlt: "A screenshot of the different publishing options in the Shorthand editor",
  },
  {
    id: 5,
    title: "Integrations",
    description:
      "Lock in your brand’s visual identity and set global styles to align content across your organisation. Standardise colours, fonts, logos, and more.",
    imageUrl: "/media/enterprise/integrations.webp",
    imageAlt: "A logo cloud of different integrations that Shorthand supports",
  },
  {
    id: 6,
    title: "Language translation",
    description:
      "Have full creative control by adding custom HTML, CSS, and JavaScript to your stories. This flexibility allows for unique visual treatments and seamless integration with third-party tools.",
    imageUrl: "/media/enterprise/translate.webp",
    imageAlt: "A photo of a translation from and to different languages",
  },
];

const partnerSuccess = [
  {
    id: 1,
    icon: faHandsHoldingDiamond,
    title: "White-glove support",
    description: (
      <>
        Get hands-on co-creation sessions, editorial insight and feedback, reliable technical assistance, and comprehensive training
        from our industry-leading support team.
      </>
    ),
  },
  {
    id: 2,
    icon: faPalette,
    title: "Design services",
    description: (
      <>
        Leverage our design experts or partner with agencies to create visually compelling content that aligns with your brand and gets
        the results you need.
      </>
    ),
  },
  {
    id: 3,
    icon: faWrench,
    title: "Custom solutions",
    description: (
      <>
        Collaborate with our network of partners for additional services, including custom development and strategic support, to
        enhance your storytelling capabilities.
      </>
    ),
  },
];

const securityAndCompliance = [
  {
    id: 1,
    icon: faLock,
    title: "Password-protected publishing",
    description: (
      <>
        Safeguard sensitive content by restricting access through password protection, ensuring only authorised viewers can engage with
        your stories.
      </>
    ),
  },
  {
    id: 2,
    icon: faKey,
    title: "Single sign-on & SCIM",
    description: (
      <>Simplified, secure access — at scale. Integrate with your identity provider or set up custom SSO including SAML and SCIM.</>
    ),
  },
  {
    id: 3,
    icon: undefined,
    image: (
      <StaticImage
        src="../../static/media/features/badge-iso27001.png"
        alt="ISO 27001 badge"
        aria-hidden="true"
        placeholder="blurred"
      />
    ),
    title: "ISO 27001 certification",
    description: (
      <>
        Publish with confidence. Shorthand’s ISO 27001 certification means your data is protected by globally recognised information
        security standards.
      </>
    ),
  },
  {
    id: 4,
    icon: undefined,
    image: <StaticImage src="../../static/media/features/badge-soc2.png" alt="SOC 2 badge" aria-hidden="true" placeholder="blurred" />,
    title: "SOC 2 compliance",
    description: (
      <>
        Our SOC 2 Type 2 compliance demonstrates our commitment to strict security standards, giving you confidence in how we protect
        data and run our operations.
      </>
    ),
  },
];

const Index: React.FC<IProps> = props => {
  const [showPortal, setShowPortal] = React.useState(false);
  const prePopulatedFields = React.useRef<Record<any, string>>({});

  function handleShowPortal(fields?: Record<any, string>): void {
    console.log("fields", fields);
    setShowPortal(true);
    prePopulatedFields.current = fields;
  }
  const portalProps = {
    type: "general-enquiry",
    subject: "general-enquiry",
    source: "marketing/home",
    prePopulatedFields: prePopulatedFields.current,
    onClose: () => {
      setShowPortal(false);
      prePopulatedFields.current = {};
    },
  };

  const cards = props.data.allFeaturedStoriesJson.nodes.map((story: any, index: number) => (
    <Card
      key={story.linkUrl || index}
      card={{
        id: index + 1,
        title: story.title,
        imageUrl: story.imageUrl?.src?.publicURL || story.imageUrl?.src?.childImageSharp?.gatsbyImageData?.images?.fallback?.src || "",
        linkUrl: story.linkUrl,
      }}
    />
  ));

  return (
    <Layout location={props.location} theme={"dark-theme"}>
      {showPortal && ReactDOM.createPortal(<Enquiry {...portalProps} />, document.getElementById("modal"))}
      <main className="marketing-content home tw-flex tw-w-full tw-flex-col tw-items-center tw-bg-black tw-text-white">
        <Helmet
          bodyAttributes={{
            class: "body-shorthand body-marketing body-marketing-home body-light hide-elevio",
          }}
        />
        <Hero
          catchline="Shorthand for Enterprise"
          title={
            <>
              Publish <span className="tw-font-PassengerSerif tw-italic">premium</span> content at scale
            </>
          }
          description={
            <>
              Join leading brands like Amazon, Honda, and Tripadvisor in creating standout digital content. Shorthand powers modern,
              web-native publications with lower overheads and higher impact.
            </>
          }
          image={
            <StaticImage
              src={`../../static/media/enterprise/enterprise-hero.webp`}
              alt="A collage of various enterprise customers' stories created with Shorthand"
              width={2432}
              height={1442}
              className="tw-w-[76rem] tw-rounded-md tw-bg-white/5 tw-shadow-2xl tw-ring-1 tw-ring-white/10"
            />
          }
          imagePlacement="left"
          actions={
            <>
              <Button asChild variant="primary-gradient" size="lg">
                <a href={CTA_LINK} className="tw-no-underline hover:tw-no-underline">
                  {CTA_TEXT}
                </a>
              </Button>
              <Button
                variant="secondary-outline"
                style={{ border: "1px solid white" }}
                size="lg"
                onClick={() =>
                  handleShowPortal({
                    interestedIn: "demo",
                    message: "booking a demo of the Shorthand platform",
                  })
                }
              >
                {DEMO_TEXT}
              </Button>
            </>
          }
        />

        <Section>
          <div className="tw-mx-auto tw-grid tw-max-w-2xl tw-grid-cols-1 tw-items-start tw-gap-x-8 tw-gap-y-10 sm:tw-gap-y-16 lg:tw-mx-0 lg:tw-max-w-none lg:tw-grid-cols-2 lg:tw-gap-y-20">
            <div className="tw-space-y-4 lg:tw-max-w-[50ch]">
              <Title as="h3">Why Shorthand?</Title>
              <Subtitle as="p" className="tw-font-PassengerSerif tw-italic tw-text-brand_scale-400">
                Create standout visual stories with up to 10x higher engagement
              </Subtitle>
              <p className="!tw-text-base/6">
                From brand publications to annual reports and employee communications, Shorthand powers modern digital storytelling
                that informs, inspires, and moves the metrics that matter.
              </p>
            </div>
            <dl className="tw-space-y-6 lg:tw-max-w-[50ch]">
              {whyShorthand.map(feature => (
                <>
                  <dt className="tw-space-x-2 tw-font-bold tw-text-[var(--foreground-color)]">
                    <Icon icon={faStar} className="tw-text-brand_scale-400" />
                    <span>{feature.title}</span>
                  </dt>
                  <dd className="!tw-mt-2 !tw-text-base/6 tw-text-[var(--foreground-color-muted)]">{feature.description}</dd>
                </>
              ))}
            </dl>
          </div>
        </Section>

        <Section>
          <TitleSection
            title="Craft great stories at scale"
            description="From premium publishing options to AI-powered layouts — discover advanced features that help you scale and create beautiful content faster."
          />
          <Carousel
            items={carouselItems.map(feature => (
              <CarouselItemWrapper>
                <div className="tw-relative tw-h-full">
                  <div className="tw-absolute tw-inset-px tw-rounded-lg tw-bg-grey_scale-800/50" />
                  <div className="tw-relative tw-flex tw-h-full tw-flex-col tw-overflow-hidden tw-rounded-[calc(theme(borderRadius.lg)+1px)]">
                    <div className="tw-px-4 tw-pb-1 tw-pt-4 sm:tw-px-10 sm:tw-pb-0 sm:tw-pt-10">
                      <p className="tw-mt-2 !tw-text-lg tw-font-medium tw-tracking-tight tw-text-[var(--foreground-color)] default-max-lg:tw-text-center">
                        {feature.title}
                      </p>
                      <p className="tw-mt-2 tw-max-w-lg !tw-text-base tw-text-[var(--foreground-color-muted)] default-max-lg:tw-text-center">
                        {feature.description}
                      </p>
                    </div>
                    <div className="tw-flex-grow"></div>
                    <div className="tw-relative tw-mt-4">
                      <div className="tw-relative tw-ml-4 tw-mr-0 tw-overflow-hidden tw-rounded-tl-xl tw-bg-grey_scale-800/50 tw-shadow-2xl tw-outline tw-outline-white/10 sm:tw-ml-10">
                        <div className="tw-relative tw-aspect-video">
                          <img
                            alt={feature.imageAlt}
                            src={feature.imageUrl}
                            className="tw-absolute tw-inset-0 tw-h-full tw-w-full tw-object-cover tw-object-center"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="tw-pointer-events-none tw-absolute tw-inset-px tw-rounded-lg tw-shadow tw-outline tw-outline-black/5" />
                </div>
              </CarouselItemWrapper>
            ))}
          />
        </Section>

        <Section>
          <div className="tw-space-y-4">
            <Title as="h3">Partners in your success</Title>
            <Subtitle as="p" className="tw-font-PassengerSerif tw-italic tw-text-brand_scale-400">
              Reduce reliance on technical resources, with support every step of the way
            </Subtitle>
            <p className="!tw-mx-0 tw-text-pretty tw-text-left !tw-text-base/6 lg:tw-max-w-[50%]">
              Shorthand lets your team create high-impact content without leaning too hard on developers or designers. And when you
              need a hand, our expert services are here to help you get it done.
            </p>
          </div>
          <div className="tw-mt-16 tw-grid tw-grid-cols-1 tw-gap-8 sm:tw-grid-cols-3">
            {partnerSuccess.map(feature => (
              <div
                className="tw-flex tw-flex-col tw-justify-start tw-gap-4 tw-rounded-2xl tw-bg-grey_scale-800/50 tw-p-8"
                key={`preview-feature-${feature.id}`}
              >
                <div className="tw-h-12 tw-w-12">
                  <Icon icon={feature.icon} className="tw-h-full tw-w-full tw-text-white" />
                </div>
                <h3 className="!tw-text-lg tw-font-semibold tw-tracking-tight tw-text-brand_scale-400">{feature.title}</h3>
                <span className="tw-max-w-[30ch] tw-text-pretty !tw-text-base/6 tw-text-[var(--foreground-color)]">
                  {feature.description}
                </span>
              </div>
            ))}
          </div>
        </Section>
        <Section>
          <div className="!tw-space-y-4">
            <Title as="h3">Rock-solid security and compliance</Title>
            <p className="!tw-mx-0 tw-text-pretty tw-text-left !tw-text-base/6 lg:tw-max-w-[50%]">
              Shorthand is enterprise-ready and proven with some of the world’s most security-conscious sectors, from government to
              finance.
            </p>
          </div>
          <div className="tw-mt-16 tw-grid tw-grid-cols-1 tw-gap-8 sm:tw-grid-cols-2 lg:tw-grid-cols-4">
            {securityAndCompliance.map(feature => (
              <div
                className="tw-flex tw-flex-col tw-justify-start tw-gap-4 tw-rounded-2xl tw-bg-grey_scale-800/50 tw-p-8"
                key={`preview-feature-${feature.id}`}
              >
                <div className="tw-h-12 tw-w-12">
                  {feature.image ? feature.image : <Icon icon={feature.icon} className="tw-h-full tw-w-full tw-text-white" />}
                </div>
                <h3 className="!tw-text-lg tw-font-semibold tw-tracking-tight tw-text-brand_scale-400">{feature.title}</h3>
                <span className="tw-max-w-[30ch] tw-text-pretty !tw-text-base/6 tw-text-[var(--foreground-color)]">
                  {feature.description}
                </span>
              </div>
            ))}
          </div>
        </Section>

        <Section>
          <TitleSection title="Trusted by thousands of teams around the world" />
          <div className="tw-mt-10 tw-flex tw-w-full tw-justify-center tw-gap-10 max-sm:!tw-gap-4">
            <div className="tw-w-[20%]">
              <a
                href="https://www.g2.com/products/shorthand/reviews?utm_source=rewards-badge"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="G2 website"
                className="tw-m-auto tw-mt-0"
              >
                <RewardsBadgeA />
              </a>
            </div>

            <div className="tw-w-[20%]">
              <a
                href="https://www.g2.com/products/shorthand/reviews?utm_source=rewards-badge"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="G2 website"
                className="tw-m-auto tw-mt-0"
              >
                <RewardsBadgeB />
              </a>
            </div>

            <div className="tw-w-[20%]">
              <a
                href="https://www.g2.com/products/shorthand/reviews?utm_source=rewards-badge"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="G2 website"
                className="tw-m-auto tw-mt-0"
              >
                <RewardsBadgeC />
              </a>
            </div>
          </div>
        </Section>

        <CaseStudyGrid />

        <Section>
          <TitleSection
            headingElement="h2"
            title={
              <>
                Get <span className="tw-font-PassengerSerif tw-italic">inspired</span>
              </>
            }
            description="See how some of the world's most recognised brands use Shorthand to build engaging visual stories."
          />
          <Carousel items={cards} />
        </Section>

        <LetsBuildFooter />
      </main>

      <Footer className="tw-mt-0" />
    </Layout>
  );
};

export default Index;

export const query = graphql`
  query HomeQuery {
    site {
      siteMetadata {
        name
        title
        tagline
        description
      }
    }
    allFeaturedStoriesJson {
      nodes {
        title
        linkUrl
        imageUrl {
          alt
          src {
            childImageSharp {
              gatsbyImageData(placeholder: BLURRED, width: 600, height: 830, quality: 80)
            }
            publicURL
          }
        }
      }
    }
  }
`;
